import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Download, RefreshCw, FileText, CheckCircle2, Clock, XCircle,
  Search, Archive, FileSpreadsheet, ChevronLeft, ChevronRight, Eye, Filter
} from 'lucide-react';
import PortalLayout from './PortalLayout';
import { getGlobalPropertyId, setGlobalProperty } from './propertySync';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const portalApi = axios.create({ baseURL: API_URL });

const formatRM = (v) => `RM ${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const StatusBadge = ({ status }) => {
  const s = (status || 'pending').toLowerCase();
  if (s === 'valid' || s === 'validated') return <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="w-3 h-3" /> Validated</span>;
  if (s === 'rejected' || s === 'invalid' || s === 'error') return <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-500 dark:text-red-400"><XCircle className="w-3 h-3" /> Rejected</span>;
  if (s === 'submitted') return <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-500 dark:text-blue-400"><RefreshCw className="w-3 h-3" /> Submitted</span>;
  return <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-500 dark:text-amber-400"><Clock className="w-3 h-3" /> Pending</span>;
};

export default function PortalReports() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState({ total: 0, validated: 0, pending: 0, rejected: 0, validated_revenue: 0 });
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [downloadingPdfId, setDownloadingPdfId] = useState(null);
  const [page, setPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const limit = 20;

  // Filters
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [propertyFilter, setPropertyFilter] = useState(() => getGlobalPropertyId() || 'all');
  const [searchQuery, setSearchQuery] = useState('');

  const token = localStorage.getItem('einvoice_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) { navigate('/einvoice/portal/auth'); return; }
    fetchProperties();
  }, []);

  useEffect(() => { fetchReport(); }, [page, dateFrom, dateTo, statusFilter, propertyFilter]);

  // Global → page sync
  useEffect(() => {
    const handler = (e) => {
      const prop = e.detail;
      setPropertyFilter(prop ? prop.id : 'all');
      setPage(1);
    };
    window.addEventListener('property-changed', handler);
    return () => window.removeEventListener('property-changed', handler);
  }, []);

  // Page → global sync
  const handlePropertyFilterChange = (val) => {
    setPropertyFilter(val);
    setPage(1);
    setGlobalProperty(val === 'all' ? null : val, properties);
  };

  const fetchProperties = async () => {
    try {
      const { data } = await portalApi.get('/api/einvoice/portal/properties', { headers });
      setProperties(data.properties || []);
    } catch (e) { console.error(e); }
  };

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (propertyFilter !== 'all') params.append('property_id', propertyFilter);
      if (searchQuery.trim()) params.append('search', searchQuery.trim());

      const { data } = await portalApi.get(`/api/einvoice/gateway/reports/invoices?${params}`, { headers });
      setInvoices(data.invoices || []);
      setTotalResults(data.total || 0);
      setSummary(data.summary || {});
    } catch (e) {
      console.error(e);
      toast.error('Failed to load report');
    } finally { setLoading(false); }
  }, [page, dateFrom, dateTo, statusFilter, propertyFilter, searchQuery]);

  const handleSearch = () => { setPage(1); fetchReport(); };

  const handleReset = () => {
    const d = new Date();
    setDateFrom(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`);
    setDateTo(d.toISOString().split('T')[0]);
    setStatusFilter('all');
    setPropertyFilter('all');
    setSearchQuery('');
    setPage(1);
  };

  const handleDownloadZip = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (propertyFilter !== 'all') params.append('property_id', propertyFilter);

      const response = await portalApi.get(`/api/einvoice/gateway/reports/download-zip?${params}`, {
        headers, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const fn = `einvoice-${dateFrom || 'all'}_to_${dateTo || 'all'}.zip`;
      link.setAttribute('download', fn);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${fn}`);
    } catch (e) {
      const msg = e.response?.status === 404 ? 'No validated invoices found for the selected period' : 'ZIP download failed';
      toast.error(msg);
    } finally { setDownloading(false); }
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (propertyFilter !== 'all') params.append('property_id', propertyFilter);

      const response = await portalApi.get(`/api/einvoice/gateway/reports/export-csv?${params}`, {
        headers, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `einvoice-report-${dateFrom}_to_${dateTo}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (e) { toast.error('CSV export failed'); }
    finally { setExporting(false); }
  };

  const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
    if (downloadingPdfId) return;
    setDownloadingPdfId(invoiceId);
    toast.info('Generating PDF\u2026');
    try {
      const res = await fetch(`${API_URL}/api/einvoice/gateway/invoices/${invoiceId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        let msg = 'Failed to download PDF. Please try again.';
        try { const err = await res.json(); msg = err.detail || msg; } catch {}
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `e-invoice-${(invoiceNumber || invoiceId).replace(/\//g, '-')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error(e.message || 'PDF download failed. Please try again.');
    } finally {
      setDownloadingPdfId(null);
    }
  };

  const totalPages = Math.ceil(totalResults / limit);

  return (
    <PortalLayout>
      <div className="p-4 sm:p-6 max-w-[1400px] mx-auto space-y-5" data-testid="portal-reports">

        {/* Page Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white" data-testid="reports-title">e-Invoice Reports</h1>
            <p className="text-slate-500 dark:text-gray-400 text-sm mt-0.5">Download validated e-Invoices submitted to LHDN (MyInvois)</p>
            <p className="text-xs font-medium text-[#4169E1] mt-1" data-testid="viewing-property-label">
              Viewing: {propertyFilter === 'all' ? 'All Properties' : (properties.find(p => p.id === propertyFilter)?.property_name || propertyFilter)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handleExportCsv} disabled={exporting}
              className="text-xs h-8 bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="export-csv-btn"
            >
              {exporting ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />}
              Export CSV
            </Button>
            <Button
              size="sm"
              onClick={handleDownloadZip} disabled={downloading}
              className="text-xs h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="download-zip-btn"
            >
              {downloading ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Archive className="w-3.5 h-3.5 mr-1.5" />}
              Download ZIP
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="report-summary-cards">
          <Card className="border-slate-200/80 dark:border-zinc-800">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-zinc-800 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-slate-500 dark:text-gray-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 dark:text-white" data-testid="summary-total">{summary.total}</p>
                <p className="text-[10px] text-slate-400 dark:text-gray-500 uppercase tracking-wide">Total Invoices</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200/80 dark:border-zinc-800">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center shrink-0">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400" data-testid="summary-validated">{summary.validated}</p>
                <p className="text-[10px] text-slate-400 dark:text-gray-500 uppercase tracking-wide">Validated</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200/80 dark:border-zinc-800">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600 dark:text-amber-400" data-testid="summary-pending">{summary.pending}</p>
                <p className="text-[10px] text-slate-400 dark:text-gray-500 uppercase tracking-wide">Pending</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200/80 dark:border-zinc-800">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center shrink-0">
                <XCircle className="w-5 h-5 text-red-500 dark:text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-500 dark:text-red-400" data-testid="summary-rejected">{summary.rejected}</p>
                <p className="text-[10px] text-slate-400 dark:text-gray-500 uppercase tracking-wide">Rejected</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-slate-200/80 dark:border-zinc-800" data-testid="report-filters">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <Label className="text-[11px] text-slate-500 dark:text-gray-400">Date From</Label>
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                  className="w-[150px] h-9 text-sm" data-testid="filter-date-from" />
              </div>
              <div>
                <Label className="text-[11px] text-slate-500 dark:text-gray-400">Date To</Label>
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                  className="w-[150px] h-9 text-sm" data-testid="filter-date-to" />
              </div>
              <div>
                <Label className="text-[11px] text-slate-500 dark:text-gray-400">Status</Label>
                <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-[140px] h-9" data-testid="filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="validated">Validated</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="submitted">Submitted</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] text-slate-500 dark:text-gray-400">Property</Label>
                <Select value={propertyFilter} onValueChange={handlePropertyFilterChange}>
                  <SelectTrigger className="w-[180px] h-9" data-testid="filter-property"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Properties</SelectItem>
                    {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.property_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[180px]">
                <Label className="text-[11px] text-slate-500 dark:text-gray-400">Search Invoice / Guest</Label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400" />
                  <Input
                    value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSearch()}
                    placeholder="Invoice no. or guest name..."
                    className="pl-8 h-9 text-sm"
                    data-testid="filter-search"
                  />
                </div>
              </div>
              <Button size="sm" className="h-9 bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSearch} data-testid="filter-search-btn">
                <Search className="w-3.5 h-3.5 mr-1" /> Search
              </Button>
              <Button variant="ghost" size="sm" className="h-9 text-slate-400 dark:text-gray-400 hover:text-white" onClick={handleReset} data-testid="filter-reset-btn">
                Reset
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Invoice Table */}
        <Card className="border-slate-200/80 dark:border-zinc-800" data-testid="report-table-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base font-semibold text-slate-800 dark:text-white">
              Invoice List
              <span className="text-sm font-normal text-slate-400 dark:text-gray-500 ml-2">({totalResults} results)</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
              </div>
            ) : invoices.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="report-invoices-table">
                  <thead>
                    <tr className="border-b border-t bg-slate-50/80 dark:bg-zinc-900/50 border-slate-200 dark:border-zinc-800">
                      <th className="text-left px-4 py-2.5 text-[11px] font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide">Invoice No</th>
                      <th className="text-left px-4 py-2.5 text-[11px] font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide">Booking ID</th>
                      <th className="text-left px-4 py-2.5 text-[11px] font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide">e-Invoice ID</th>
                      <th className="text-left px-4 py-2.5 text-[11px] font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide">Buyer</th>
                      <th className="text-left px-4 py-2.5 text-[11px] font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide">Invoice Date</th>
                      <th className="text-right px-4 py-2.5 text-[11px] font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide">Amount</th>
                      <th className="text-center px-4 py-2.5 text-[11px] font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide">Status</th>
                      <th className="text-left px-4 py-2.5 text-[11px] font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide">Validation Date</th>
                      <th className="text-center px-4 py-2.5 text-[11px] font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map(inv => (
                      <tr key={inv.id} className="border-b border-slate-100 dark:border-zinc-800/50 hover:bg-slate-50/50 dark:hover:bg-zinc-900/30 transition-colors">
                        <td className="px-4 py-2.5 font-mono text-sm text-blue-600 dark:text-blue-400">{inv.invoice_number}</td>
                        <td className="px-4 py-2.5 text-xs font-mono text-slate-600 dark:text-gray-300" data-testid={`booking-id-${inv.id}`}>
                          {inv.pms_booking_id || inv.booking_ref || <span className="text-slate-300 dark:text-gray-600">-</span>}
                        </td>
                        <td className="px-4 py-2.5 text-xs font-mono text-slate-500 dark:text-gray-400 max-w-[160px] truncate">
                          {inv.lhdn_uuid || <span className="text-slate-300 dark:text-gray-600">-</span>}
                        </td>
                        <td className="px-4 py-2.5 text-sm text-slate-700 dark:text-gray-300 max-w-[160px] truncate">{inv.guest_name || '-'}</td>
                        <td className="px-4 py-2.5 text-sm text-slate-500 dark:text-gray-400">{inv.invoice_date || '-'}</td>
                        <td className="px-4 py-2.5 text-sm text-right font-medium text-slate-800 dark:text-white">{formatRM(inv.total_amount)}</td>
                        <td className="px-4 py-2.5 text-center"><StatusBadge status={inv.status} /></td>
                        <td className="px-4 py-2.5 text-xs text-slate-400 dark:text-gray-500">
                          {inv.validation_timestamp ? new Date(inv.validation_timestamp).toLocaleDateString('en-MY') : '-'}
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost" size="sm"
                              className="h-7 px-2 text-[11px] text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                              onClick={() => handleDownloadPdf(inv.id, inv.invoice_number)}
                              disabled={downloadingPdfId === inv.id}
                              data-testid={`download-pdf-${inv.id}`}
                            >
                              {downloadingPdfId === inv.id
                                ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> PDF</>
                                : <><Download className="w-3 h-3 mr-1" /> PDF</>}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-16" data-testid="reports-empty">
                <FileText className="w-10 h-10 mx-auto text-slate-200 dark:text-gray-700 mb-2" />
                <p className="text-sm font-medium text-slate-600 dark:text-gray-300">No invoices found</p>
                <p className="text-xs text-slate-400 dark:text-gray-500 mt-1">Try adjusting your date range or filters</p>
              </div>
            )}
            {/* Pagination - Always visible */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-zinc-800" data-testid="reports-pagination">
              <p className="text-xs text-slate-500 dark:text-gray-400">
                {totalResults > 0 ? `Showing ${(page - 1) * limit + 1}–${Math.min(page * limit, totalResults)} of ${totalResults} invoices` : 'No records'}
              </p>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)} data-testid="reports-page-prev">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                {totalPages > 0 && [...Array(Math.min(totalPages, 5))].map((_, i) => {
                  const p = page <= 3 ? i + 1 : page - 2 + i;
                  if (p < 1 || p > totalPages) return null;
                  return (
                    <Button key={p} variant={p === page ? "default" : "outline"} size="sm"
                      className={`h-7 w-7 p-0 text-xs ${p === page ? 'bg-blue-600 text-white' : ''}`}
                      onClick={() => setPage(p)} data-testid={`reports-page-${p}`}>
                      {p}
                    </Button>
                  );
                })}
                <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)} data-testid="reports-page-next">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Info Footer */}
        <div className="text-center text-xs text-slate-400 dark:text-gray-500 pb-4">
          Only validated invoices are included in the ZIP download. PDFs are generated with LHDN verification QR codes.
        </div>
      </div>
    </PortalLayout>
  );
}
